import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileDashedIcon, Info, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { cn } from '@/lib/utils';
import { getCampaignContentRest } from '@/modules/campaigns/services/campaigns-rest.service';
import { CampaignReportNav, type CampaignReportTab } from '../components/campaign-report-nav';
import { CampaignReportFunnel } from '../components/campaign-report-funnel';
import { CampaignReportRates } from '../components/campaign-report-rates';
import {
  HEATMAP_SCALE,
  heatmapBucketFor,
  LinkHeatmapOverlay,
} from '../components/link-heatmap-overlay';
import { ReportAlertStrip } from '../components/report-alert-strip';
import { ReportExportControl } from '../components/report-export-control';
import { ReportRangeControl } from '../components/report-range-control';
import { DeliveryDataNotice, ReportRequestState } from '../components/report-request-state';
import { EmailPreviewSkeleton } from '../components/report-skeleton';
import { RecipientEventsLookup } from '../components/recipient-events-lookup';
import { DeliverabilityBody } from './reports-deliverability';
import {
  useCampaignAnalyticsReport,
  useCampaignLinks,
  useReportOverview,
} from '../hooks/use-analytics';
import { useReportRange } from '../hooks/use-report-range';
import type { CampaignReport, LinkRow } from '../types/analytics-api.types';
import type { LinkReportRow } from '../types/reports.types';
import { analyticsErrorStatus } from '../utils/analytics-errors';
import { fmtNum, fmtPct, fmtShortDate } from '../utils/report-format';

type CampaignReportPageProps = {
  tab: CampaignReportTab;
};

export function CampaignReportPage({ tab }: CampaignReportPageProps) {
  const { campaignId = '' } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { range } = useReportRange(90);
  const sendJobId = params.get('sendJobId') || undefined;
  const qs = params.toString();
  const reportQuery = useCampaignAnalyticsReport(campaignId, sendJobId);
  const overview = useReportOverview({ from: range.from, to: range.to });

  const switchCampaign = (id: string) => {
    const base =
      tab === 'links'
        ? `/reports/campaigns/${id}/links`
        : tab === 'deliverability'
          ? `/reports/campaigns/${id}/deliverability`
          : `/reports/campaigns/${id}`;
    navigate(`${base}${qs ? `?${qs}` : ''}`);
  };

  if (analyticsErrorStatus(reportQuery.error) === 404) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
        <div className="text-base font-semibold text-neutral-900">Campaign not found</div>
        <p className="max-w-sm text-neutral-600">
          This campaign doesn’t exist, or belongs to a different organization.
        </p>
        <Button asChild>
          <Link to="/reports/overview">Back to overview</Link>
        </Button>
      </div>
    );
  }

  const report = reportQuery.data;
  const noSend = !!report && report.totals.sent === 0 && report.audience.sendable === 0;

  return (
    <div className="flex h-full min-h-0 flex-col gap-5 overflow-y-auto p-4 sm:p-6">
      <ReportRequestState
        isLoading={reportQuery.isLoading}
        error={
          reportQuery.isError && analyticsErrorStatus(reportQuery.error) !== 404
            ? reportQuery.error
            : undefined
        }
        skeleton="campaign"
      />
      {report ? (
        <CampaignReportNav
          campaign={{
            itemId: report.campaign.itemId,
            name: report.campaign.name,
            status: report.campaign.status,
            sentAt: report.campaign.sentAt,
            lastEventAt: report.lastEventAt,
          }}
          options={(overview.data?.campaigns ?? []).map((row) => ({
            itemId: row.itemId,
            name: row.name,
            status: row.status,
          }))}
          active={tab}
          onCampaignChange={switchCampaign}
        />
      ) : null}

      {report && noSend ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-neutral-200 bg-white p-10 text-center shadow-sm">
          <PaperPlaneTilt className="size-8 text-neutral-500" aria-hidden />
          <div className="text-base font-semibold text-neutral-900">No send data yet</div>
          <p className="max-w-[380px] text-neutral-600">
            {report.campaign.name} hasn’t sent, so there’s nothing to report yet — not a zeroed
            report, genuinely no data.
          </p>
          <Button asChild className="gap-2">
            <Link to="/campaigns">Back to campaigns</Link>
          </Button>
        </div>
      ) : null}

      {report && !noSend && tab === 'performance' ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <ReportRangeControl defaultDays={90} />
            <ReportExportControl
              report="campaign-report"
              scopeId={report.campaign.itemId}
              formats={['csv', 'pdf']}
              getFilters={() => ({
                campaignId: report.campaign.itemId,
                from: range.from,
                to: range.to,
                sendJobId,
              })}
            />
          </div>
          <PerformancePanel
            report={report}
            from={range.from}
            to={range.to}
            complaintThreshold={overview.data?.thresholds.complaintRate ?? 0.001}
          />
        </>
      ) : null}
      {report && !noSend && tab === 'links' ? (
        <LinksPanel campaignId={report.campaign.itemId} from={range.from} to={range.to} />
      ) : null}
      {report && !noSend && tab === 'deliverability' ? (
        <CampaignDeliverability
          campaignId={report.campaign.itemId}
          name={report.campaign.name}
          from={range.from}
          to={range.to}
          days={range.days}
        />
      ) : null}
    </div>
  );
}

function PerformancePanel({
  report,
  from,
  to,
  complaintThreshold,
}: {
  report: CampaignReport;
  from: string;
  to: string;
  complaintThreshold: number;
}) {
  const alerts = [...report.warnings]
    .map((warning) => ({
      code: warning.code,
      severity: (warning.code === 'complaint-rate-high' ? 'critical' : 'warning') as
        'critical' | 'warning',
      value: warning.value,
      threshold: warning.threshold,
      campaignId: report.campaign.itemId,
      campaignName: report.campaign.name,
    }))
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1));

  return (
    <div className="flex flex-col gap-5">
      {report.deliveryDataAvailable ? null : <DeliveryDataNotice />}
      <ReportAlertStrip alerts={alerts} />
      <CampaignReportFunnel
        audienceTotal={report.audience.total}
        sendable={report.audience.sendable}
        sent={report.totals.sent}
        delivered={report.totals.delivered}
        uniqueOpens={report.totals.uniqueOpens}
        uniqueClicks={report.totals.uniqueClicks}
        skipped={report.totals.skipped}
        skips={report.skips}
        balances={report.audience.balances}
      />
      <CampaignReportRates
        rates={report.rates}
        trackOpens={report.tracking.trackOpens}
        trackClicks={report.tracking.trackClicks}
        deliveryDataAvailable={report.deliveryDataAvailable}
        complaintThreshold={complaintThreshold}
      />
      <TimelineChart report={report} />
      <RecipientEventsLookup campaignId={report.campaign.itemId} from={from} to={to} />
    </div>
  );
}

function TimelineChart({ report }: { report: CampaignReport }) {
  const data = report.timeline.map((point) => ({
    ...point,
    label:
      report.timelineInterval === 'hour'
        ? new Date(point.bucket).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
          })
        : fmtShortDate(point.bucket.slice(0, 10)),
  }));
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-semibold text-neutral-900">Send timeline</h3>
      <p className="mb-3 text-xs text-neutral-500">
        {report.timelineInterval === 'hour'
          ? 'Hourly for the first 72 hours after the first send.'
          : 'Daily after the first 72 hours.'}
      </p>
      {data.length === 0 ? (
        <p className="py-8 text-center text-xs text-neutral-500">No timeline buckets yet.</p>
      ) : (
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="#E7E8EA" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#666', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: '#707070', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={40}
                allowDecimals={false}
              />
              <Tooltip formatter={(value: number, name: string) => [fmtNum(value), name]} />
              <Line
                type="monotone"
                dataKey="delivered"
                stroke="#1baf7a"
                strokeWidth={2}
                dot={false}
              />
              <Line type="monotone" dataKey="opens" stroke="#2a78d6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="clicks" stroke="#eb6834" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function CampaignDeliverability({
  campaignId,
  name,
  from,
  to,
  days,
}: {
  campaignId: string;
  name: string;
  from: string;
  to: string;
  days: number;
}) {
  const [groupBy, setGroupBy] = useState<'domain' | 'day'>('domain');
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ReportRangeControl defaultDays={90} />
        <ReportExportControl
          report="deliverability"
          scopeId={campaignId}
          formats={['csv']}
          getFilters={() => ({ campaignId, from, to, groupBy })}
        />
      </div>
      <DeliverabilityBody
        from={from}
        to={to}
        days={days}
        campaignId={campaignId}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        scopeLabel={name}
      />
    </>
  );
}

function toHeatRow(row: LinkRow): LinkReportRow {
  return {
    linkId: row.linkId,
    url: row.url,
    label: row.label,
    position: row.position,
    isUnsubscribe: row.isUnsubscribe,
    clicks: row.clicks,
    uniqueClicks: row.uniqueClicks,
    share: row.clickShare ?? 0,
  };
}

function LinksPanel({ campaignId, from, to }: { campaignId: string; from: string; to: string }) {
  const [mode, setMode] = useState<'table' | 'heat'>('table');
  const [device, setDevice] = useState<'all' | 'mobile' | 'desktop'>('all');
  const [q, setQ] = useState('');
  const [hotLinkId, setHotLinkId] = useState<string | null>(null);
  const deviceClass = device === 'all' ? undefined : device;
  const links = useCampaignLinks(campaignId, { from, to, deviceClass });
  const report = links.data;
  const contentVersion = report?.content?.version;
  const content = useQuery({
    queryKey: ['reports', 'link-content', campaignId, contentVersion ?? 'none'],
    queryFn: () => {
      if (contentVersion == null) throw new Error('Content version is required');
      return getCampaignContentRest(campaignId, contentVersion);
    },
    enabled: contentVersion != null,
  });

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (report?.items ?? []).filter(
      (row) =>
        !needle ||
        row.url.toLowerCase().includes(needle) ||
        row.label.toLowerCase().includes(needle)
    );
  }, [report?.items, q]);

  const maxClicks = Math.max(0, ...(report?.items ?? []).map((row) => row.clicks));
  const heatRows = (report?.items ?? []).map(toHeatRow);
  const ranked = [...(report?.items ?? [])].slice(0, 8);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <ReportRangeControl defaultDays={90} />
          <Segmented
            label="Links view"
            value={mode}
            onChange={(next) => {
              setMode(next);
              setHotLinkId(null);
            }}
            options={[
              { key: 'table', label: 'Table' },
              { key: 'heat', label: 'Heatmap' },
            ]}
          />
          <Segmented
            label="Device"
            value={device}
            onChange={setDevice}
            options={[
              { key: 'all', label: 'All' },
              { key: 'mobile', label: 'Mobile' },
              { key: 'desktop', label: 'Desktop' },
            ]}
          />
        </div>
        <ReportExportControl
          report="campaign-links"
          scopeId={campaignId}
          formats={['csv']}
          getFilters={() => ({
            campaignId,
            from,
            to,
            deviceClass,
          })}
        />
      </div>

      <ReportRequestState isLoading={links.isLoading} error={links.error} skeleton="links" />

      <div className="flex items-start gap-2.5 rounded-lg border border-neutral-200 bg-white p-3.5">
        <Info className="mt-0.5 size-4 shrink-0 text-neutral-500" aria-hidden />
        <p className="text-xs text-neutral-600">
          Clicks are counted <strong>per link occurrence</strong>, so the same URL used twice is two
          rows. Shares can add up to more than 100% because the total is distinct people who clicked
          anything. Image-proxy clicks count only in All devices. Remote images in the heatmap are
          loaded so the email matches the send; scripts are not.
        </p>
      </div>

      {report && mode === 'table' ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by URL"
              aria-label="Filter links by URL"
              className="h-9 max-w-[260px] text-sm"
            />
            <span className="text-xs text-neutral-500">
              {fmtNum(rows.length)} of {fmtNum(report.items.length)} links ·{' '}
              {fmtNum(report.totals.clicks)} clicks · {fmtNum(report.totals.uniqueClicks)} unique
              {device !== 'all' ? ` · ${device} only` : ''}
            </span>
          </div>
          <LinkTable rows={rows} maxClicks={maxClicks} hotLinkId={hotLinkId} />
          <UnplacedLinks rows={report.unplaced} />
        </>
      ) : null}

      {report && mode === 'heat' && !report.content ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-neutral-200 bg-white p-8 text-center shadow-sm">
          <FileDashedIcon className="size-7 text-neutral-400" aria-hidden />
          <p className="text-sm font-medium text-neutral-900">No stored content for this send</p>
          <p className="max-w-sm text-xs text-neutral-500">
            The heatmap needs the HTML of the version that was sent. This report has no content
            version, so the counts stay in the table.
          </p>
          <Button type="button" variant="outline" className="mt-1" onClick={() => setMode('table')}>
            Show the table
          </Button>
        </div>
      ) : null}

      {report && mode === 'heat' && report.content ? (
        content.isLoading ? (
          <EmailPreviewSkeleton />
        ) : content.data?.html ? (
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
              <LinkHeatmapOverlay
                html={content.data.html}
                rows={heatRows}
                maxClicks={maxClicks}
                hotLinkId={hotLinkId}
                onHover={setHotLinkId}
              />
            </div>
            <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
              <h3 className="text-base font-semibold text-neutral-900">Most clicked</h3>
              <p className="mb-2 mt-0.5 text-xs text-neutral-500">
                Hover a row to find it in the email.
              </p>
              <ul className="flex flex-col">
                {ranked.map((row) => {
                  const bucket = heatmapBucketFor(row.clicks, maxClicks);
                  const color = HEATMAP_SCALE[bucket];
                  const hot = hotLinkId === row.linkId;
                  return (
                    <li key={row.linkId}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-2.5 border-b border-neutral-100 py-2 text-left last:border-b-0',
                          hot && 'rounded-md bg-[#F5F9FE]'
                        )}
                        onMouseEnter={() => setHotLinkId(row.linkId)}
                        onMouseLeave={() => setHotLinkId(null)}
                      >
                        <span
                          className="flex h-[22px] w-[26px] shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold tabular-nums"
                          style={{ background: color.bg, color: color.fg }}
                        >
                          {fmtNum(row.clicks)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-medium text-neutral-900">
                            {row.label || row.url}
                          </span>
                          <span className="block truncate text-xs text-neutral-500">
                            link {(row.position ?? 0) + 1} · {fmtNum(row.uniqueClicks)} unique
                          </span>
                        </span>
                        <span className="shrink-0 font-mono text-xs text-neutral-600">
                          {fmtPct(row.clickShare)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {report.items.length > ranked.length ? (
                <p className="mt-2 text-xs text-neutral-500">
                  {fmtNum(report.items.length - ranked.length)} more in the table view.
                </p>
              ) : null}
              <UnplacedLinks rows={report.unplaced} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-600">
            The sent version could not be loaded. The table still has every click count.
          </p>
        )
      ) : null}
    </div>
  );
}

function LinkTable({
  rows,
  maxClicks,
  hotLinkId,
}: {
  rows: LinkRow[];
  maxClicks: number;
  hotLinkId: string | null;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-neutral-100">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">Link</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Clicks
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Unique
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.linkId}
                className={cn(
                  'border-t border-neutral-100',
                  hotLinkId === row.linkId && 'bg-[#F5F9FE]'
                )}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-neutral-900">
                    {row.label}
                    {row.isUnsubscribe ? (
                      <span className="ml-2 text-xs font-normal text-neutral-500">unsubscribe</span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 max-w-[420px] truncate font-mono text-xs text-neutral-500">
                    {row.url}
                  </div>
                  <div className="mt-1 text-xs text-neutral-500">
                    {row.position == null
                      ? 'Position unknown'
                      : `Link ${row.position + 1} in the email`}
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className="h-full rounded-full bg-[#2a78d6]"
                      style={{ width: `${maxClicks ? (row.clicks / maxClicks) * 100 : 0}%` }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtNum(row.clicks)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtNum(row.uniqueClicks)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{fmtPct(row.clickShare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UnplacedLinks({ rows }: { rows: LinkRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4">
      <h3 className="text-sm font-semibold text-neutral-900">Unplaced links</h3>
      <p className="mt-0.5 text-xs text-neutral-500">
        Clicks whose anchors are not in the sent HTML. Their position is not guessed.
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {rows.map((row) => (
          <li key={row.linkId} className="text-xs text-neutral-700">
            {row.label || row.url} · {fmtNum(row.clicks)} clicks
          </li>
        ))}
      </ul>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ key: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <div
      className="inline-flex items-center overflow-hidden rounded-md border border-neutral-300 bg-white"
      role="group"
      aria-label={label}
    >
      {options.map((option, index) => (
        <button
          key={option.key}
          type="button"
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
          className={cn(
            'px-3 py-1.5 text-[13px] font-medium',
            index > 0 && 'border-l border-neutral-300',
            value === option.key
              ? 'bg-primary-600 text-white'
              : 'bg-white text-neutral-700 hover:bg-neutral-50'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
